/* eslint-disable max-lines */
import { select } from '@angular-redux/store';
import {
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
  ViewChild,
  Injector,
} from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import {
  cloneDeep,
  concat,
  each,
  filter,
  find,
  findIndex,
  get,
  groupBy,
  has,
  head,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  isPlainObject,
  keys,
  last,
  map,
  merge,
  noop,
  omit,
  omitBy,
  pickBy,
  reject,
  remove,
  set,
  some,
  split,
  startCase,
  toPairs,
  union,
  unset,
  invoke,
  size,
  forEach,
  reduce,
  assign,
  debounce,
  chain,
  lt,
  isUndefined,
  lte,
  assignIn,
} from 'lodash';
import * as fp from 'lodash/fp';

import {
  GridApi,
  GridOptions,
  RowNode,
  NodeChildDetails,
  RowGroupOpenedEvent,
} from 'ag-grid-community';
import {
  BehaviorSubject,
  Observable,
  observableCombineLatest,
  observableFrom,
  observableOf,
  ReplaySubject,
  rx,
  Subject,
  Subscription,
} from '@shared/rx';

import {
  BUTTON_FLEX_ORDER,
  DEFAULT_TREE_CHILDREN_PROPERTY,
  ENTITY_ID_PATH,
  HIDE_COLUMN_CONDITIONS,
  NO_THEME_CLASS,
  TIME_FORMATS,
  XUX_LIST_THEME_CLASS,
} from '@config/index';
import {
  BaseService,
  EntityServiceProvider,
  MODIFIED_DATE_COLUMN,
  PermissionService,
  STATE_SELECTORS,
  StateService,
} from '@shared/services';
import {
  IBaseEntityType,
  IDocumentErrorMessage,
  IDocumentInfoMessage,
  IDocumentOperationOptions,
  ENTITY_TYPES,
  FilterModel,
  IFilterQuery,
  IQueryModificatorsData,
  IRequestPayload,
  SortModelField,
  ToolbarButtonDefinition,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  IXVIListColumn,
  IToolbarButtonDefinition,
  TooltipIconConfig,
  XuxGridActionPayload,
  IEntityConfig,
  IOrderByUseLabelPath,
  DependentRequestParam,
  IDependentRequestPlainParam,
  IApiListResponse,
  GridExpandedState,
  GridCellClass,
  CELL_RENDERER,
} from '@shared/types';
import { APIActions, UiActions } from '@store/actions';
import { IUIListDateRange, IUIListState } from '@store/reducers/ui';
import { toMoment } from '@app/shared/utils/date-utils';
import {
  BTN_AUTO_RESTOCKS,
  BTN_CANCEL_OPEN_STAND_WORKSHEET,
  BTN_CREATE,
  BTN_EDIT,
  BTN_EXPORT,
  BTN_PRINT,
  BTN_PRINT_OPENING_SHEETS,
  BTN_PRINT_SHEET,
  BTN_REFRESH,
  BTN_REMOVE,
  BTN_SELECT_EVENT,
  BTN_SELECT_SITE,
  BTN_SELECT_SITE_AND_EVENT,
  BTN_SHOW_DELETED_ITEMS,
  BTN_VIEW_DETAILS,
  BUTTON_ACTIONS,
  getExpandOrCollapseBtn,
} from '@text-content/button-templates';
import {
  composeQuery,
  hasColumnsQuickFilterOption,
} from './xux-list.column-routines';
import { xviCustomGridComponents } from '@components/xvi-grid/custom-components';
import { DEFAULT_ENTITIES_COLUMNS } from '@shared/services/base/entities-constants';
import {
  LIST_TREE_GRID_CLASS,
  MAX_PAGE_SIZE,
  MULTI_DOCUMENT_EDITING_MAX_DOCUMENTS_LIMIT,
} from '@config/global-constants';
/* eslint-disable max-len */
import { DELETED_VIEW_TITLE } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/grid-view.type';
import { ENTITY_NAME_CONFIG, getSingularEntityName } from '@shared/configs';
import { STATUS_VALUE } from '@app/shared/types/enums/status.enum';
import { COMMON_WARN_MESSAGE_CLASS } from '@app/shared/constants/common-warn-grid-setting';
import {
  expandOrCollapseGrid,
  handleGridOverlay,
  updateGridSortFilter,
} from '@app/shared/helpers';
import { XuxGridComponent } from '@geniuspos/ui-shared/modules/xdm-shared-components/components';
import { XuxGridCustomComponent } from '@app/components/xux-grid-custom';
import { DefaultViewFieldConfig } from '@app/shared/types/common/default-view-query';
import { DASHBOARD_ROUTE } from '@app/app.routes';
import {
  composeDefaultViewDocumentQuerySetting,
  composeDefaultViewFilterModelSetting,
} from '@app/shared/services/filtered-order/filtered.util';
import { I18n } from '@app/i18n/strings-keys';
import { TranslateService } from '@ngx-translate/core';
import { XVII18nService } from '@app/i18n/services';
import { WIDGETS_STATIC_TEXT_CONTENT } from '@app/i18n/constants/static-text-content.constants';
import {
  getAllRowsAfterFilterAndSort,
  getCollapsedOrExpanded,
  isLeaf,
} from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { DEFAULT_VALUE_FIELD } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/constants/local';
import { INTERNAL_GRID_ROW_ID } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component.constants';

type ExtendedBaseService = BaseService;

const deletedModeListState: IUIListState = {
  filterModel: {
    is_active: {
      filter: [false],
      headerText: ['Deleted'],
      type: 'containsAny',
    },
  },
  selectedColumns: ['updated_at'],
  viewName: DELETED_VIEW_TITLE,
};

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-list',
  templateUrl: 'xux-list.container.html',
  styleUrls: ['xux-list.container.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XUXListContainerComponent<
    TEntityService extends ExtendedBaseService = ExtendedBaseService,
  >
  implements OnInit, OnDestroy
{
  @select(['ui', 'list']) public viewConfig$: Observable<IUIListState>;
  // this value is shared in all entity module.
  @select(['ui', 'list', 'extraFilterModel', 'dateRange'])
  public dateRange$: Observable<IUIListDateRange>;
  @select(['ui', 'loader']) public mainLoader$: Observable<boolean>;
  @select(['ui', 'reloadListConfig'])
  public reloadListConfig$: Observable<number>;
  @select(['userSession', 'isIframeMode'])
  public isIframeMode$: Observable<boolean>;
  @select(['tools', 'history']) public history$: Observable<string[]>;

  @ViewChild('listGrid', { static: false }) public xuxGridComponent:
    | XuxGridComponent
    | XuxGridCustomComponent;

  public I18n = I18n;
  public staticI18nTextContent = this.i18nService.translateStaticTextContent({
    xuxGrid: WIDGETS_STATIC_TEXT_CONTENT.xuxGrid,
  });

  public viewConfig: IUIListState = {};
  public showEventSelector: boolean = false;
  public showSiteSelector: boolean = false;
  public showPrintButton: boolean = false;
  public showExportButton: boolean = false;
  public showSiteAndEventSelector: boolean = false;
  public showPrintOpeningSheets: boolean = false;
  public showPrintSheet: boolean = false;
  public showCancelOpeningSheetBtn: boolean = false;

  public entityService: TEntityService;
  public entityType: ENTITY_TYPES;
  public entityTitle: string;
  public headerName: string;
  public description: string;
  public disableItemsCountLabel: boolean;
  public titleTooltipIconConfig$: BehaviorSubject<TooltipIconConfig> = null;

  public columnDefinitions: IXVIListColumn[] = [];
  public selectedItems: any[] = [];

  public subscriptions: Subscription[] = [];

  public warningMessage$: BehaviorSubject<string>;
  public warningLabelClass: string = COMMON_WARN_MESSAGE_CLASS;

  // TODO: consider storing entityData also in the UI list store to avoid complex selectors
  public dependentEntitiesData: Partial<
    Record<ENTITY_TYPES, IApiListResponse<IBaseEntityType>>
  >;

  protected dependentData$: BehaviorSubject<Record<string, any>> =
    new BehaviorSubject<Record<string, any>>({});

  public title$: BehaviorSubject<string>;
  public pageCount$: Observable<number>;
  public items$: Observable<any[]>;

  public total$: Observable<number>;
  public pageSideTotal: number = 0;
  public isDocumentInfoMessageShow$: Observable<boolean>;

  public filterModel: FilterModel;
  public sortModel: SortModelField[];

  public canAdd: boolean;
  public canDelete: boolean;
  public canManage: boolean;
  public canView: boolean;
  public canViewDeleted: boolean;

  public inShowDeletedMode: boolean;
  public isHiddenRefreshButton: boolean;

  public gridReady$: Subject<void> = new Subject<void>();

  public isLoaderVisible$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public actionButtons: IToolbarButtonDefinition[] = [];
  public rowActionButtons: ToolbarButtonDefinition[] = [];
  public createButton: ToolbarButtonDefinition;
  public printButton: ToolbarButtonDefinition;
  public exportButton: ToolbarButtonDefinition;
  public eventSelectorButton: ToolbarButtonDefinition;
  public siteSelectorButton: ToolbarButtonDefinition;
  public siteAndEventSelectorButton: ToolbarButtonDefinition;
  public printOpeningSheetsButton: ToolbarButtonDefinition;
  public printSheetButton: ToolbarButtonDefinition;
  public cancelStandWorkSheetButton: ToolbarButtonDefinition;
  public autoRestocksButton: ToolbarButtonDefinition;
  public classes: string = '';
  public autoRestockBtnTooltip: string =
    I18n.InfoMessages.AutoRestockWillCreatePreEventBaseOnLatestEvent;
  public expandedNodeIds = new Set<string>();

  // properties for grid
  public gridOptions: IEntityConfig['list']['gridOptions'] = {
    orderControls: false,
    multiselect: true,
    animateRows: false,
    showViewSelector: true,
    searchPlaceholder: this.translateService.instant(I18n.Common.Search),
    showPagination: true,
    valueField: ENTITY_ID_PATH,
    disableColumnSelector: false,
    gridHeight: '100%',
    overlayNoRowsTemplate: this.i18nService.instant(I18n.Common.NoRowsToShow),
  };

  public userGridOptions: GridOptions = {
    enableServerSideSorting: true,
    rowClassRules: {
      'background-danger': (data: any): boolean => {
        return (
          get(data, 'data.status') ===
          get(this.entityService, 'config.list.failedItemStatusValue', '')
        );
      },
    },
    defaultColDef: {
      cellClassRules: {
        [GridCellClass.HIDDEN_GROUP_ICON]: (params): boolean => {
          return (
            get(params, ['node', 'group']) &&
            !isLeaf(params) &&
            lte(get(params, ['node', 'allChildrenCount'], 0), 0)
          );
        },
      },
    },
  };

  public get treeTotalLevel(): number {
    return this.gridOptions.treeTotalLevel ?? Number.POSITIVE_INFINITY;
  }

  public xviCustomGridComponents: object = xviCustomGridComponents;

  public listTotalItemsLabel$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.translateService.instant(I18n.Forms.GridMessages.Items),
    );

  public gridApi: GridApi;

  public infoMessageData$: Observable<
    (IDocumentInfoMessage | IDocumentErrorMessage)[]
  >;

  public refreshList$: ReplaySubject<any> = new ReplaySubject();

  public isStandWorksheetRouter: boolean = false;
  public isHistoryQuantitiesRouter: boolean = false;
  public useCustomGrid: boolean = false;
  public isTreeViewMode: boolean = false;
  public firstEnter: boolean = true;
  public hasUserToggledExpand: boolean = false;

  public selfDefaultActions: Partial<
    Record<
      XUX_GRID_ACTIONS,
      (action: XuxGridAction, xuxList: XUXListContainerComponent) => void
    >
  > = {};

  private actionButtonsConfig: IToolbarButtonDefinition[] = [];
  private listViewUpdate$: ReplaySubject<IUIListState> = new ReplaySubject();

  private initialUpdatedAtTitle: string;

  // eslint-disable-next-line max-params
  public constructor(
    public route: ActivatedRoute,
    public uiActions: UiActions,
    public apiActions: APIActions,
    public permissionService: PermissionService,
    public entityServiceProvider: EntityServiceProvider,
    public stateService: StateService,
    protected renderer: Renderer2,
    protected router: Router,
    protected translateService: TranslateService,
    protected i18nService: XVII18nService,
    protected injector: Injector,
  ) {}

  public ngOnInit(): void {
    this.uiActions.setUiTheme(XUX_LIST_THEME_CLASS);
    this.handleGridOverlayWithLoader();

    const entityConnection$ = observableCombineLatest([
      this.route.data,
      this.reloadListConfig$,
    ]).pipe(
      rx.map(([data, changesNumber]: [Data, number]) => [
        get(data, 'ENTITY_TYPE', ''),
        changesNumber,
      ]),
      rx.distinctUntilChanged(),
      rx.filter(Boolean),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      rx.mergeMap(([entityType, changesNumber]: [string, number]) =>
        this.connectToEntity(entityType),
      ),
      rx.tap(() => this.initListTitle()),
      rx.tap(() => this.initActionButtonsConfig()),
      rx.share(),
    );

    // We need to split that observable due to bug in RxJs
    // https://github.com/ReactiveX/rxjs/issues/3346
    const viewConfigHandler$ = observableCombineLatest([
      this.viewConfig$,
      /**
       * wait until entityConnection$ allows us to ignore all events until the first connection
       * to entity has been made.
       */
      entityConnection$,
    ]).pipe(
      /**
       * filter only view events for the currently connected entity
       */
      rx.filter(
        ([viewConfig, entityType]) => viewConfig.entityType === entityType,
      ),
      rx.withLatestFrom(this.history$),
      /** passing only the first argument "viewConfig" */
      rx.map(([[viewConfig], history]) => {
        let defaultViewConfig = {
          viewConfig: {
            filterModel: null,
          },
          documentQuery: null,
        };
        if (
          !isNil(
            get(
              this.entityService,
              ['config', 'list', 'defaultViewFieldsSetting'],
              null,
            ),
          )
        ) {
          defaultViewConfig = this.composeDefaultViewQueryData(history);
        }

        return { viewConfig, defaultViewConfig, history };
      }),
      rx.tap(({ viewConfig, defaultViewConfig, history }) => {
        const isPriorityUrlRoutingParams =
          this.checkIsPriorityUrlRoutingParams(history);

        // keep this reference is the key to keep the default rendering of the list sorting and filtering
        this.viewConfig = viewConfig;

        if (!isEmpty(viewConfig.viewName)) {
          // keep this reference is the key to keep the default rendering of the list sorting and filtering
          set(
            viewConfig,
            'viewName',
            this.translateService.instant(
              isEqual(viewConfig.viewName, 'View')
                ? I18n.Common.View
                : viewConfig.viewName,
            ),
          );
        }

        this.viewConfig.filterModel = isPriorityUrlRoutingParams
          ? {
              ...this.viewConfig.filterModel,
              ...defaultViewConfig?.viewConfig?.filterModel,
            }
          : {
              ...defaultViewConfig?.viewConfig?.filterModel,
              ...this.viewConfig.filterModel,
            };

        if (this.canView && this.canViewDeleted) {
          this.applyShowDeletedMode();
        } else {
          set(this.viewConfig, 'defaultViews', []);
        }
      }),
      rx.tap(
        ({ viewConfig }) =>
          (this.gridOptions.showSearch = hasColumnsQuickFilterOption(
            viewConfig,
            this.columnDefinitions,
          )),
      ),
      rx.map(({ viewConfig, defaultViewConfig }) =>
        this.composeFilterQuery(viewConfig, defaultViewConfig?.documentQuery),
      ),
      rx.distinctUntilChanged(isEqual),
      rx.debounceTime(0),
    );

    const dateRangeSource$ = this.dateRange$.pipe(
      rx.distinctUntilChanged(isEqual),
      rx.startWith(null),
    );

    this.subscriptions.push(
      observableCombineLatest([
        viewConfigHandler$.pipe(rx.share()),
        // eslint-disable-next-line import/no-deprecated
        this.refreshList$.asObservable().pipe(rx.startWith(null)),
        dateRangeSource$,
      ])
        .pipe(
          rx.debounceTime(0),
          rx.filter(([, , dateRange]) => {
            if (this.shouldTriggerInitialDateRange(dateRange)) {
              this.triggerInitialDateRange();

              return false;
            }

            return true;
          }),
          rx.tap(([, , dateRange]) => {
            if (dateRange && get(this.entityService, 'dateRangeField')) {
              this.updateDateRangeFilter(
                dateRange.dateStart,
                dateRange.dateEnd,
              );
              this.restoreCalendarValue(dateRange);
            }
          }),
          rx.map(([query, , dateRange]) => {
            if (dateRange && get(this.entityService, 'dateRangeField')) {
              return this.composeFilterQuery(this.viewConfig);
            }

            return query;
          }),
          rx.mergeMap((query) => this.getDocumentsForExpansion(query)),
        )
        .subscribe(() => {}),
    );

    // subscription the page side search rows count
    this.subscriptions.push(
      this.items$.subscribe((items) => {
        this.pageSideTotal =
          (this.isTreeViewMode
            ? this.getTreeItemsCount({ items })
            : size(items)) || 0;
      }),
    );

    /**
     *  the following code allow us to combine several sequential view updates
     * into a single update to avoid excessive actions
     */
    const update$ = this.listViewUpdate$.asObservable().pipe(rx.share());
    this.subscriptions.push(
      update$
        .pipe(
          rx.buffer(update$.pipe(rx.debounceTime(100))),
          rx.map((updates) => merge({}, ...updates)),
        )
        .subscribe((combinedUpdate) =>
          this.updateListViewDataProxy(combinedUpdate),
        ),
    );

    const infoMessageHandler = get(
      this.entityService,
      'getInfoMessageInformation',
    );
    if (isFunction(infoMessageHandler)) {
      this.infoMessageData$ = observableFrom(
        this.entityService.getInfoMessageInformation(),
      );
    }

    const listUserGridOptions = get(
      this.entityService,
      'config.list.userGridOptions',
      null,
    );
    if (listUserGridOptions) {
      this.userGridOptions = Object.assign(
        {},
        this.userGridOptions,
        listUserGridOptions,
      );
    }

    const listGridOptions = get(
      this.entityService,
      'config.list.gridOptions',
      null,
    );
    if (listGridOptions) {
      this.gridOptions = Object.assign(
        {
          getNodeChildDetails: this.getNodeChildDetails.bind(this),
        },
        this.gridOptions,
        listGridOptions,
      );
    }

    const warningMessage$ = get(
      this.entityService,
      'config.list.warningMessage$',
      null,
    );
    if (warningMessage$) {
      this.warningMessage$ = warningMessage$;
    }
  }

  public getExpandableNodeId(node?: RowNode | null): string {
    const data = get(node, 'data', null);

    return data ? this.getRowNodeId(data) || '' : get(node, 'id', '');
  }

  /**
   * this is override xux-grid & xux-grid-custom used function to support tree data,
   * if the row has children,
   * return the children and other info, otherwise return undefined to let grid treat it as normal row
   */
  public getNodeChildDetails(rowItem: IBaseEntityType): NodeChildDetails {
    const keepNodeOwnExpanded = get(
      this.gridOptions,
      'keepNodeOwnExpanded',
      false,
    );

    const treeChildrenPropName = get(
      this.gridOptions,
      'treeChildrenPropertyName',
      DEFAULT_TREE_CHILDREN_PROPERTY,
    );

    if (has(rowItem, treeChildrenPropName)) {
      const nodeId = this.getRowNodeId(rowItem);
      const nodes = this.gridApi ? this.gridApi.getRenderedNodes() : [];
      const node = find(nodes, { id: nodeId });

      return {
        expanded: get(
          node,
          'expanded',
          keepNodeOwnExpanded ? null : !this.xuxGridComponent.allCollapsed,
        ),
        group: get(rowItem, 'group', true),
        children: rowItem[treeChildrenPropName],
        key: rowItem.entity_id,
      };
    }
  }

  public getRowNodeId(data: IBaseEntityType): string {
    if (isFunction(this.userGridOptions.getRowNodeId)) {
      return this.userGridOptions.getRowNodeId(data);
    }

    const valueField = get(this, 'options.valueField', DEFAULT_VALUE_FIELD);

    const rowId = get(data, valueField);

    if (!isUndefined(rowId)) {
      return rowId;
    }

    return has(data, INTERNAL_GRID_ROW_ID)
      ? get(data, INTERNAL_GRID_ROW_ID, '')
      : rowId;
  }

  public getTreeItemsCount(params: {
    items: IBaseEntityType[];
    level?: number;
  }): number {
    const { items, level = 0 } = params;

    return reduce(
      items,
      (sum, item) =>
        has(item, 'children') && lt(level, this.treeTotalLevel)
          ? sum +
            this.getTreeItemsCount({
              items: item.children,
              level: level + 1,
            })
          : sum + 1,
      0,
    );
  }

  public translateStaticText() {
    return this.i18nService.translateStaticTextContent(
      this.staticI18nTextContent,
    );
  }

  protected getViewSortModelField(orderBy: string): SortModelField[] {
    return fp.flow(
      fp.split(','),
      fp.map<string, SortModelField>(this.createSortModel.bind(this)),
    )(orderBy);
  }

  protected createSortModel(sortRuleSet: string): SortModelField {
    const sortRule: string[] = split(sortRuleSet.trim(), /\s+/);

    return {
      colId: head(sortRule),
      sort: last(sortRule),
    };
  }

  protected updateListViewDataProxy(data: IUIListState): void {
    this.uiActions.updateListViewData(data);
  }

  /**
   * handle customize expanded state for tree data if needed
   */
  public handleCustomizeExpanded(params: { gridApi: GridApi }): void {
    if (!this.isTreeViewMode) {
      return;
    }
    const enableDefaultExpandFn = get(
      this.gridOptions,
      'enableDefaultExpandFn',
      false,
    );
    const customizeExpandFn = get(
      this.entityService,
      'config.list.gridOptions.customizeExpandFn',
      enableDefaultExpandFn ? this.defaultExpandFn.bind(this) : noop,
    );

    customizeExpandFn({ ...params, isInitExpand: !this.hasUserToggledExpand });
  }

  public defaultExpandFn(params: {
    gridApi: GridApi;
    isInitExpand?: boolean;
  }): boolean {
    const { gridApi, isInitExpand = true } = params;

    if (!gridApi || gridApi.getDisplayedRowCount() <= 0) {
      return false;
    }

    if (!isInitExpand) {
      if (this.expandedNodeIds.size > 0) {
        let hasExpandedAny = false;
        const staleIds: string[] = [];

        this.expandedNodeIds.forEach((id) => {
          const node = gridApi.getRowNode(id);

          if (!node || get(node, 'allChildrenCount', 0) <= 0) {
            staleIds.push(id);

            return;
          }

          if (!node.expanded) {
            node.setExpanded(true);
            hasExpandedAny = true;
          }
        });

        // Prune stale IDs that no longer exist in the current grid
        staleIds.forEach((id) => this.expandedNodeIds.delete(id));

        return hasExpandedAny;
      }

      return false;
    }

    const firstNode = gridApi.getDisplayedRowAtIndex(0);

    if (!firstNode || firstNode.allChildrenCount <= 0) {
      return false;
    }

    gridApi.setRowNodeExpanded(firstNode, true);

    return true;
  }

  public composeFilterQuery(
    viewConfig: IUIListState,
    documentViewQuery: IFilterQuery = {},
  ): IFilterQuery {
    const defaultQueryOrderBy: string | null = cloneDeep(
      get(this.entityService, 'config.list.documentQuery.orderby', null),
    );

    if (isEmpty(viewConfig.sortModel) && defaultQueryOrderBy) {
      viewConfig.sortModel = this.getViewSortModelField(defaultQueryOrderBy);
    }

    const defaultQuery: IFilterQuery = cloneDeep(
      get(this.entityService, 'config.list.documentQuery', {}),
    );
    const queryModificatorsData: IQueryModificatorsData = {
      listView: viewConfig,
      columnConfig: this.columnDefinitions,
      inShowDeletedMode: this.inShowDeletedMode,
    };
    const configDefaultPageSize = get(
      this.entityService,
      'config.list.gridOptions.defaultPageSize',
      null,
    );

    if (configDefaultPageSize) {
      queryModificatorsData.listView.pageSize = configDefaultPageSize;
    }

    const shouldIgnoreServerSide = this.isClientSideListFilteringEnabled();

    if (shouldIgnoreServerSide) {
      defaultQuery.top = defaultQuery?.top ?? MAX_PAGE_SIZE;
    }

    return composeQuery(
      merge({}, defaultQuery, documentViewQuery),
      queryModificatorsData,
      {
        ignoreFilter: shouldIgnoreServerSide,
        orderByUseLabelPaths: this.getOrderByUseLabelPaths(),
        ignoreSort: shouldIgnoreServerSide,
      },
    );
  }

  public getOrderByUseLabelPaths(): IOrderByUseLabelPath[] {
    const columns = get(this.entityService, 'config.list.columns', []);

    return map(
      filter(columns, (column) => {
        return (
          get(column, 'sortUseLabelPath', false) &&
          !isNil(get(column, 'labelPath'))
        );
      }),
      (column) => ({
        path: get(column, 'path', ''),
        labelPath: get(column, 'labelPath', ''),
      }),
    );
  }

  public checkIsPriorityUrlRoutingParams(history: string[]): boolean {
    const navigateURLValue = get(this.entityService, 'config.uiRoute');

    const moduleRouteRegex = new RegExp(`^\\/${navigateURLValue}`);

    const routeRegex = new RegExp(
      `^\\/external_login;navigateURL=${navigateURLValue}(?:\\?(.+))?`,
    );

    const curModuleUri = findIndex(history, (route) =>
      moduleRouteRegex.test(route),
    );

    const curPageUri = findIndex(history, (route) => routeRegex.test(route));

    return (
      curModuleUri > -1 && curPageUri > -1 && curModuleUri + 1 === curPageUri
    );
  }

  public setInShowDeletedMode(value: boolean): void {
    const inShowDeletedModeClass = 'show-deleted-view';

    if (value) {
      this.renderer.addClass(document.body, inShowDeletedModeClass);
    } else {
      this.renderer.removeClass(document.body, inShowDeletedModeClass);
    }

    this.inShowDeletedMode = value;
    this.uiActions.setIsDeleteViewMode(value);
  }

  public getIsShowLoad(): Observable<boolean> {
    return observableCombineLatest([
      this.isLoaderVisible$,
      this.mainLoader$,
    ]).pipe(
      rx.map(([isLoaderVisible, mainLoader]) => !mainLoader && isLoaderVisible),
    );
  }

  /**
   * will hidden overlay when loader is visible
   * and show overlay when loader is hidden
   */
  public handleGridOverlayWithLoader(): void {
    this.subscriptions.push(
      this.getIsShowLoad()
        .pipe(
          rx.tap((showLoader) => {
            if (showLoader) {
              this.gridApi.hideOverlay();
            } else {
              handleGridOverlay(this);
            }
          }),
        )
        .subscribe(noop),
    );
  }

  public async getDocumentsForExpansion(
    query: IFilterQuery,
  ): Promise<[IBaseEntityType[], IFilterQuery]> {
    this.isLoaderVisible$.next(true);

    const shouldFetchFullList = this.isClientSideListFilteringEnabled();

    const documentOperationOptions = get(
      this.entityService,
      'config.list.documentOperationOptions',
      {},
    );

    const options: IDocumentOperationOptions = {
      ...documentOperationOptions,
      ...(shouldFetchFullList
        ? {
            fetchFullListData:
              documentOperationOptions?.fetchFullListData ?? true,
          }
        : {}),
      noLoader: true,
    };

    const dependentEntitiesRes = await this.fetchDependentEntities()
      .pipe(
        rx.tap((dependentRes) => {
          this.dependentEntitiesData = dependentRes;
        }),
      )
      .toPromise();

    const { items: documents } = await this.fetchList(query, {
      ...options,
      dependentEntitiesData: dependentEntitiesRes,
    });
    this.isLoaderVisible$.next(false);

    return [documents, query];
  }

  private shouldTriggerInitialDateRange(dateRange: any): boolean {
    if (!get(this.entityService, 'dateRangeField')) {
      return false;
    }

    const defaultDateStart = get(this, ['calendarValue', 'value', 'dateStart']);

    return isNil(get(dateRange, 'dateStart', null)) && !isNil(defaultDateStart);
  }

  private triggerInitialDateRange(): void {
    const dateStart = get(this, ['calendarValue', 'value', 'dateStart'], null);
    const dateEnd = get(this, ['calendarValue', 'value', 'dateEnd'], null);
    const type = get(this, ['calendarValue', 'type']);

    this.uiActions.updateListViewData({
      extraFilterModel: {
        dateRange: {
          dateStart,
          dateEnd: dateEnd || dateStart,
          type,
        },
      },
    });
  }

  private restoreCalendarValue(dateRange: IUIListDateRange): void {
    if (!dateRange.dateStart || !get(this, 'calendarValue')) {
      return;
    }

    const currentStart = get(this, ['calendarValue', 'value', 'dateStart']);
    const currentEnd = get(this, ['calendarValue', 'value', 'dateEnd']);

    if (
      isEqual(currentStart, dateRange.dateStart) &&
      isEqual(currentEnd, dateRange.dateEnd)
    ) {
      return;
    }

    set(this, 'calendarValue', {
      type: dateRange.type || get(this, ['calendarValue', 'type']),
      value: {
        dateStart: dateRange.dateStart,
        dateEnd: dateRange.dateEnd,
      },
    });
  }

  private updateDateRangeFilter(
    dateStart: Date | string,
    dateEnd: Date | string,
  ): void {
    const baseDocumentFilter: string = get(
      this.entityService,
      'baseDocumentFilter',
      '',
    );
    const dateRangeField: string = get(
      this.entityService,
      'dateRangeField',
      '',
    );
    const filterParts: string[] = [];

    if (baseDocumentFilter) {
      filterParts.push(baseDocumentFilter);
    }

    if (dateStart && dateRangeField) {
      const fromDate = toMoment(dateStart).startOf('day').toISOString();
      const toDate = toMoment(dateEnd || dateStart)
        .endOf('day')
        .toISOString();

      filterParts.push(
        `${dateRangeField} gt '${fromDate}' and ${dateRangeField} lt '${toDate}'`,
      );
    }

    set(
      this.entityService,
      'config.list.documentQuery.filter',
      filterParts.join(' and '),
    );
  }

  private isClientSideListFilteringEnabled(): boolean {
    return (
      get(
        this.entityService,
        'config.list.userGridOptions.enableServerSideFilter',
        this.userGridOptions.enableServerSideFilter,
      ) === false
    );
  }

  /**
   * fetch dependent entities data
   * and will not fail the whole request if one or more entity(s) fetch fails
   *
   * @param requestEntities
   * @returns dependent entities data observable { entityType: IApiListResponse<IBaseEntityType>}
   */
  public fetchDependentEntities(
    requestEntities?: DependentRequestParam[],
  ): Observable<IDocumentOperationOptions['dependentEntitiesData']> {
    const listDependentEntities = get(
      this.entityService,
      'config.list.dependentEntities',
      [],
    ) as DependentRequestParam[];

    const dependentEntities = requestEntities || listDependentEntities;

    if (isEmpty(dependentEntities)) {
      return observableOf({});
    }

    return observableFrom(dependentEntities).pipe(
      rx.mergeMap((dependentEntity) => {
        const fixedRequestOptions: IDocumentOperationOptions = {
          noLoader: true,
        };

        const defaultRequestOptions: IDocumentOperationOptions = {
          ...fixedRequestOptions,
          fetchFullListData: true,
        };

        const defaultQuery: IFilterQuery = { top: MAX_PAGE_SIZE };

        const isComplicatedRequest: boolean = isPlainObject(dependentEntity);

        const responseMapper = get(
          dependentEntity,
          'options.responseMapper',
          (res) => res,
        );
        const applyResponseMapper = (
          response: IApiListResponse<IBaseEntityType>,
        ): any =>
          isFunction(responseMapper) ? responseMapper(response) : response;

        let entityKey: string;
        let requestObs: Observable<any>;

        if (isComplicatedRequest) {
          // custom dependent request (non-ENTITY_TYPES)
          const customRequestFactory: Function = get(
            dependentEntity,
            'request$',
          );
          const isCustomDependentRequest: boolean =
            isFunction(customRequestFactory);

          if (isCustomDependentRequest) {
            entityKey = get(dependentEntity, 'entityKey');
            requestObs = customRequestFactory({
              injector: this.injector,
            });
          } else {
            const {
              entityType: dependentEntityType,
              query: dependentQuery = defaultQuery,
              options: dependentOptions = defaultRequestOptions,
            } = dependentEntity as IDependentRequestPlainParam;
            const entityService =
              this.entityServiceProvider.getService(dependentEntityType);
            const fetchListMethod =
              entityService?.[
                get(dependentOptions, 'customizeFetchListMethod', 'fetchList')
              ]?.bind(entityService);

            entityKey = dependentEntityType;
            requestObs = fetchListMethod(dependentQuery, {
              ...defaultRequestOptions,
              ...dependentOptions,
              ...fixedRequestOptions,
            });
          }
        } else {
          entityKey = dependentEntity as ENTITY_TYPES;
          requestObs = this.entityServiceProvider
            .getService(dependentEntity)
            .fetchList({ ...defaultQuery }, { ...defaultRequestOptions });
        }

        return requestObs.pipe(
          rx.take(1),
          rx.map((response: any) => ({
            entityType: entityKey,
            response: applyResponseMapper(response),
          })),
          // avoid failing the whole request if one entity fetch fails
          rx.catchError(() =>
            observableOf({
              entityType: entityKey,
              response: null,
            }),
          ),
        );
      }),
      rx.reduce(
        (dependentRes, curEntityRes) => ({
          ...dependentRes,
          [curEntityRes.entityType]: curEntityRes.response,
        }),
        {},
      ),
    );
  }

  public fetchList(
    query: IFilterQuery = {},
    options: IDocumentOperationOptions = { noLoader: true },
  ): Promise<any> {
    if (
      !isNil(options.continueFetchCondition) &&
      !options.continueFetchCondition(query)
    ) {
      return Promise.resolve({});
    }
    // Document request should have similar bulk options
    const extendedQuery: IFilterQuery = {
      ...query,
    };
    const composedPayload: IRequestPayload = this.composeMasterQuery(
      extendedQuery,
      options,
    );

    return this.apiActions.fetchList(
      this.entityService.config.ENTITY_TYPE,
      composedPayload.query,
      composedPayload.options,
    );
  }

  public applyShowDeletedMode(): void {
    this.setInShowDeletedMode(
      has(this.viewConfig.filterModel, 'is_active') || this.inShowDeletedMode,
    );
    const updatedAtColumn = find(this.columnDefinitions, {
      path: 'updated_at',
    });

    if (isEmpty(this.initialUpdatedAtTitle)) {
      this.initialUpdatedAtTitle = get(updatedAtColumn, 'title') as string;
    }

    if (updatedAtColumn) {
      updatedAtColumn.title = this.inShowDeletedMode
        ? 'Deleted Date'
        : this.initialUpdatedAtTitle;
    }

    this.gridOptions = {
      ...this.gridOptions,
      showRowOptions: this.inShowDeletedMode,
    };
  }

  public async onGridAction(action: XuxGridAction): Promise<void> {
    const payload = action.payload;
    const actionType = action.type;

    this.invokeConfiguredAction(action);

    switch (actionType) {
      case XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED:
        this.filterModelChangedByGrid(payload.filterModel);
        break;
      case XUX_GRID_ACTIONS.SORT_MODEL_CHANGED:
        this.sortModelChangedByGrid(payload.sortModel);
        break;

      case XUX_GRID_ACTIONS.ACTION_BUTTON_CLICKED:
        const button = payload.value;
        this.actionButtonClicked(button, payload.rowData);
        break;

      case XUX_GRID_ACTIONS.PAGE_SIZE_CHANGED:
        this.updateListViewData({
          pageSize: payload.pageSize,
          currentPage: 1,
        });
        break;

      case XUX_GRID_ACTIONS.CURRENT_PAGE_CHANGED:
        this.uiActions.setCurrentPage(
          Number.parseInt(payload.currentPage as any, 10),
        );
        break;

      case XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED:
        this.selectedItems = payload.rowData;
        this.updateActionButtonsStatus();
        break;

      case XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED:
        // enableServerSideFilter is false and undefined should call handleSearch
        if (!this.userGridOptions.enableServerSideFilter) {
          this.handleSearch(payload);
        } else {
          updateGridSortFilter(this.xuxGridComponent, payload.value);
        }
        break;
      case XUX_GRID_ACTIONS.ROW_DOUBLE_CLICKED:
        this.selectedItems = payload.rowData;
        this.actionButtonClicked('edit', payload.rowData);
        break;

      case XUX_GRID_ACTIONS.LIST_VIEW_RESET:
        this.setInShowDeletedMode(false);
        this.uiActions.restoreDefaultListView(this.entityType);
        break;

      case XUX_GRID_ACTIONS.GRID_READY:
        this.gridApi = action.payload.gridApi;
        this.gridReady$.next();

        const onRowGroupOpened = get(
          this.gridApi,
          'gridOptionsWrapper.gridOptions.onRowGroupOpened',
        );

        set(
          this.gridApi,
          'gridOptionsWrapper.gridOptions.onRowGroupOpened',
          (event: RowGroupOpenedEvent): void => {
            if (isFunction(onRowGroupOpened)) {
              onRowGroupOpened(event);
            }

            this.hasUserToggledExpand = true;
            const nodeId = this.getExpandableNodeId(event.node);

            if (
              isEmpty(nodeId) ||
              get(event, 'node.allChildrenCount', 0) <= 0
            ) {
              return;
            }

            if (event.node.expanded) {
              this.expandedNodeIds.add(nodeId);

              return;
            }

            this.expandedNodeIds.delete(nodeId);
          },
        );

        const context = get(this.gridApi, [
          'gridOptionsWrapper',
          'gridOptions',
          'context',
        ]);

        if (context) {
          // update context
          set(context, 'isTreeViewMode', this.isTreeViewMode);
        }

        break;

      case XUX_GRID_ACTIONS.LIST_VIEW_LOAD:
        this.uiActions.restoreUserListView(payload.value);
        break;

      case XUX_GRID_ACTIONS.LIST_VIEW_SAVE:
        this.uiActions.saveUserListView(payload.value);
        break;

      case XUX_GRID_ACTIONS.LIST_VIEW_REMOVE:
        const message = this.i18nService.t(
          I18n.ConfirmMessages.RemoveListViewConfirmMessage,
          {
            viewName: payload.value,
          },
        );
        if ((await this.uiActions.confirm(message)).confirmed) {
          this.uiActions.removeUserListView(payload.value);
        }
        break;
      case XUX_GRID_ACTIONS.COLUMN_SELECTION_CHANGED:
        this.updateListViewData(payload);
        break;
      case XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED:
        this.handleItemsArrayChanged();
        break;
      default:
    }
  }

  public invokeConfiguredAction(action: XuxGridAction): void {
    const actionType = action.type;

    const actionMapping = assign(
      {},
      this.selfDefaultActions,
      get(this.entityService, ['config', 'list', 'eventHandlers'], {}),
    );

    // invoke additional handlers from widgetData if present - add ability to control grid from formRules
    invoke(actionMapping, actionType, action, this);
  }

  public ngOnDestroy(): void {
    map(this.entityService?.beforeDestroyListMethods, (func) =>
      func.call(this.entityService, this),
    );
    this.uiActions.updateListViewData({ currentPage: 1 });
    this.uiActions.removeNotificationsOnPageLeave();
    this.uiActions.setUiTheme(NO_THEME_CLASS);
    if (isFunction(this.entityService?.clearDocumentMessages)) {
      this.entityService.clearDocumentMessages();
    }
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription?.unsubscribe(),
    );
    this.resetPredefinedFilterModel();
    this.isLoaderVisible$.next(false);
  }

  public async connectToEntity(entityType: string): Promise<string> {
    this.apiActions.storeClearItems(entityType);
    this.entityService =
      this.entityServiceProvider.getService<TEntityService>(entityType);
    map(this.entityService.beforeStartListMethods, (func) =>
      func.call(this.entityService, this),
    );
    // TODO: Find a way to use the same value for entityTitle and entityType to use only one name from one source
    const entityTitle = get(
      this.entityService,
      'config.list.entityTitle',
      null,
    );
    this.disableItemsCountLabel = get(
      this.entityService,
      'config.list.disableItemsCountLabel',
      false,
    );
    this.titleTooltipIconConfig$ = get(
      this.entityService,
      'config.list.titleTooltipIconConfig$',
      null,
    );
    this.entityType = get(this.entityService, 'config.ENTITY_TYPE', null);
    this.entityTitle = startCase(entityTitle || this.entityType);
    const customListTitle = get(
      ENTITY_NAME_CONFIG,
      [this.entityType, 'customListTitle'],
      null,
    );
    this.headerName =
      customListTitle ??
      `${this.i18nService.translate(getSingularEntityName(this.entityType))} ${this.i18nService.translate(I18n.Common.List)}`;
    this.description = get(this.entityService, 'config.list.description', null);
    const useEntityNameForListTotal = get(
      this.entityService,
      'config.list.useEntityNameForListTotal',
      false,
    );
    const listItemsLabel = get(
      this.entityService,
      'config.list.listItemsLabel',
      null,
    );

    if (useEntityNameForListTotal && isEmpty(listItemsLabel)) {
      this.listTotalItemsLabel$.next(
        this.translateService.instant(I18n.Forms.GridMessages.EntityTitles, {
          entityTitle: this.entityTitle,
        }),
      );
    }

    if (!isEmpty(listItemsLabel)) {
      this.listTotalItemsLabel$.next(
        this.translateService.instant(listItemsLabel),
      );
    }

    this.isHiddenRefreshButton = get(
      this.entityService,
      'config.list.isHiddenRefreshButton',
      false,
    );

    if (isEmpty(this.entityType)) {
      this.uiActions.showErrorToasterMessage(
        this.i18nService.instant(I18n.ErrorMessages.CouldNotConnectToEntity),
      );

      return null;
    }

    const entityNameToCheckPermissions = get(
      this.entityService,
      'config.entityNameToCheckPermissions',
      entityType,
    );

    const canAddFormEntityConfig: IEntityConfig['list']['canAdd'] = get(
      this.entityService,
      ['config', 'list', 'canAdd'],
      (_, __) => this.permissionService.canAdd(entityNameToCheckPermissions),
    );

    this.canAdd = isFunction(canAddFormEntityConfig)
      ? canAddFormEntityConfig(
          entityNameToCheckPermissions,
          this.permissionService,
        )
      : canAddFormEntityConfig;
    this.canDelete = this.permissionService.canDelete(
      entityNameToCheckPermissions,
    );
    this.canManage = this.permissionService.canManage(
      entityNameToCheckPermissions,
    );
    this.canView = this.permissionService.canView(entityNameToCheckPermissions);
    this.canViewDeleted = this.permissionService.canViewDeleted();
    this.isStandWorksheetRouter = isEqual(
      this.stateService.getState().router,
      '/stand-worksheet',
    );
    this.isHistoryQuantitiesRouter = isEqual(
      this.stateService.getState().router,
      '/historical-quantities',
    );
    this.isTreeViewMode = get(
      this.entityService,
      'config.list.gridOptions.treeViewMode',
      false,
    );
    this.classes = get(this.entityService, ['config', 'list', 'class'], '');

    if (this.isTreeViewMode && !includes(this.classes, LIST_TREE_GRID_CLASS)) {
      this.classes = isEmpty(this.classes)
        ? LIST_TREE_GRID_CLASS
        : `${LIST_TREE_GRID_CLASS} ${this.classes}`;
    }

    this.useCustomGrid = get(
      this.entityService,
      ['config', 'list', 'useCustomGrid'],
      false,
    );

    this.setColumnDefinitions();
    this.uiActions.restoreEntityListView(entityType);

    // TODO: consider storing entityData also in the UI list store to avoid complex selectors
    this.pageCount$ = this.stateService.select([
      'entities',
      entityType,
      'page_count',
    ]);
    this.items$ = this.stateService.select(['entities', entityType, 'items']);

    this.total$ = this.stateService.select(['entities', entityType, 'total']);

    return entityType;
  }

  public filterModelChangedByGrid(filterModel: FilterModel): void {
    if (isEqual(filterModel, this.filterModel)) return;

    this.filterModel = this.inShowDeletedMode
      ? { ...filterModel, ...deletedModeListState.filterModel }
      : filterModel;

    const listState: IUIListState = {
      filterModel: this.filterModel,
      currentPage: 1,
    };

    if (this.inShowDeletedMode) {
      listState.selectedColumns = deletedModeListState.selectedColumns;
    }

    this.updateListViewData(listState);

    if (!this.userGridOptions.enableServerSideFilter) {
      handleGridOverlay(this.xuxGridComponent);
      this.updatePageSideTotal();
    }
  }

  public getAddBtnLogic(): boolean {
    if (this.isStandWorksheetRouter) {
      return !!this.createButton && this.canAdd && !this.canManage;
    }
    if (this.isHistoryQuantitiesRouter) {
      return false;
    }

    return !!this.createButton && this.canAdd;
  }

  public getAddBtnGroupLogic(): boolean {
    if (this.isStandWorksheetRouter) {
      return !!this.createButton && this.canAdd && this.canManage;
    }
    if (this.isHistoryQuantitiesRouter) {
      return !!this.createButton && this.canAdd;
    }

    return false;
  }

  public sortModelChangedByGrid(sortModel: SortModelField[]): void {
    if (isEqual(sortModel, this.sortModel)) return;
    this.sortModel = sortModel;
    this.updateListViewData({ sortModel });
  }

  public async editButtonClick(
    items: IBaseEntityType[] = this.selectedItems,
  ): Promise<void> {
    const item: IBaseEntityType = head(items);
    const id = await this.getDocumentIdForEdit(item);
    const entityService = this.entityServiceProvider.getService(
      this.entityType,
    );
    const needGetUpdateItemsStatus = get(
      entityService,
      'needGetUpdateItemsStatus',
      [STATUS_VALUE.Open],
    );

    if (id) {
      const defaultParams = {
        ...(entityService.config?.editor?.enableEntryDuplicate && {
          showDuplicate: true,
        }),
      };

      this.apiActions.openDocumentEditor(
        this.entityType,
        id,
        null,
        includes(
          [ENTITY_TYPES.TRANSFER_GROUP, ENTITY_TYPES.INVENTORY_COUNT_GROUP],
          this.entityType,
        ) ||
          !includes(
            needGetUpdateItemsStatus,
            get(item, 'status', STATUS_VALUE.Open),
          )
          ? defaultParams
          : {
              ...defaultParams,
              get_updated_items: true,
            },
      );
    }
  }

  public async getDocumentIdForEdit(item: IBaseEntityType): Promise<string> {
    return this.getIdFromDocument(item);
  }

  public getIdFromDocument(doc: IBaseEntityType): string {
    return get(doc, '_id', null);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async removeButtonClick(
    items,
    handler?: (items) => Promise<boolean>,
  ): Promise<void> {
    // The logic for removing moster docs was introduced for modifier entity for backward compatibility
    let needRefresh: boolean;
    try {
      needRefresh = await handler(items);
    } catch (error) {
      needRefresh = false;
    }

    if (needRefresh) {
      this.refresh();
    }
  }

  public refresh(): void {
    this.refreshList$.next(null);
  }

  public createBtnClick(): void {
    this.apiActions.clearCurrentDocument(this.entityType);
    this.apiActions.openDocumentEditor(this.entityType, 'add');
  }

  public onSelectedItemsChange(data: IBaseEntityType[]): void {
    this.selectedItems = data;
  }

  public viewDetailsButtonClick(rowData: object[]): void {
    const documentId = get(rowData, '0._id', '');
    const uiRoute = get(this.entityService, 'config.uiRoute');

    if (documentId && uiRoute) {
      this.apiActions.openDocumentEditor(this.entityType, documentId);
    }
  }

  public showDeletedButtonClick(): void {
    this.setInShowDeletedMode(true);

    this.updateListViewData({
      ...deletedModeListState,
      viewName: this.translateService.instant(I18n.Common.DeletedItemsListView),
    });
  }

  public updateListViewData(data: IUIListState): void {
    this.listViewUpdate$.next(data);
  }

  public isServerFilter(
    enableServerSideFilter: boolean | undefined = null,
  ): boolean {
    return isNil(enableServerSideFilter) || enableServerSideFilter;
  }

  public composeDefaultViewQueryData(history: string[]): any {
    const defaultViewFieldsSetting = get(
      this.entityService,
      ['config', 'list', 'defaultViewFieldsSetting'],
      null,
    );

    const filterModelFieldsSetting: DefaultViewFieldConfig[] = get(
      defaultViewFieldsSetting,
      'filterModelSetting',
      null,
    );

    const commonValidateMessage: string = get(
      defaultViewFieldsSetting,
      'validateMessage',
      null,
    );

    const requiredFields: string[] = get(
      defaultViewFieldsSetting,
      ['requiredSetting', 'fields'],
      [],
    );

    const filterDocumentQuerySetting: DefaultViewFieldConfig = get(
      defaultViewFieldsSetting,
      'filterQuerySetting',
      null,
    );

    const navigateURLValue = get(this.entityService, 'config.uiRoute');

    const routeRegex = new RegExp(
      `^\\/external_login;navigateURL=${navigateURLValue}(?:\\?(.+))?`,
    );

    const curPageUri = find(history, (route) => routeRegex.test(route));

    const pageUri = curPageUri?.match(routeRegex)[1];

    const urlSearchParams = isNil(pageUri) ? {} : new URLSearchParams(pageUri);

    this.composeDefaultViewRequiredFieldsSetting({
      requiredFields,
      urlSearchParams,
      commonValidateMessage,
    });

    const filterModel: FilterModel = composeDefaultViewFilterModelSetting(
      filterModelFieldsSetting,
      urlSearchParams,
    );

    const documentQuery: IFilterQuery = composeDefaultViewDocumentQuerySetting(
      filterDocumentQuerySetting,
      urlSearchParams,
    );

    return {
      viewConfig: {
        filterModel,
      },
      documentQuery,
    };
  }

  public composeDefaultViewRequiredFieldsSetting({
    requiredFields,
    urlSearchParams,
    commonValidateMessage,
  }): void {
    forEach(requiredFields, (requiredField: string) => {
      const requiredData = urlSearchParams.get(requiredField);

      if (isNil(requiredData) && this.firstEnter) {
        const message = get(
          requiredField,
          ['validateMessage', 'required'],
          commonValidateMessage,
        );

        if (!isNil(message)) {
          this.uiActions.showErrorToasterMessage(message);
        }

        this.firstEnter = false;

        this.router.navigate([DASHBOARD_ROUTE]);

        return;
      }
    });
  }

  /**
   * Method handles the click events on the grid buttons
   *
   * @param btn - grid button
   */
  public handleClick(btn: ToolbarButtonDefinition): void {
    const value = btn.value || btn.caption;

    if (isFunction(btn.click)) btn.click(value);
  }

  protected setColumnDefinitions(): void {
    let columnDefinitions = get(
      this.entityService,
      ['config', 'list', 'columns'],
      [],
    );
    const isDefaultColumnsVisible = get(
      this.entityService,
      ['config', 'list', 'isDefaultColumnsVisible'],
      true,
    );

    columnDefinitions = this.populateDefaultEntitiesColumns(
      columnDefinitions,
      isDefaultColumnsVisible,
    );

    columnDefinitions = this.handleGroupColumnsParams(columnDefinitions);

    this.columnDefinitions = this.filterHiddenColumns(columnDefinitions);
  }

  protected handleGroupColumnsParams(
    columns: IXVIListColumn[],
  ): IXVIListColumn[] {
    if (!this.isTreeViewMode) {
      return columns;
    }

    return map(columns, (column) => {
      if (
        !get(column, 'showRowGroup', false) ||
        !isEqual(
          get(column, 'cellRenderer'),
          CELL_RENDERER.ag_group_cell_renderer,
        )
      ) {
        return column;
      }

      return assignIn(column, {
        cellRendererParams: {
          ...get(column, 'cellRendererParams', {}),
          // is hidden children count of this column text
          suppressCount: true,
          // extend to support shown hidden children count with tooltip in tree data
          innerRenderer: (params) => {
            const count = get(params, 'node.allChildrenCount', 0);
            const noChildrenTrailTips = this.i18nService.t(
              get(params, ['noChildrenTrailTips'], ''),
            );

            if (lte(count, 0) && !isEmpty(noChildrenTrailTips)) {
              return `${params.value ?? ''} (${noChildrenTrailTips})`;
            }

            return params.value;
          },
        },
      });
    });
  }

  protected initListTitle(): void {
    this.title$ = get(
      this.entityService,
      'config.list.titleObs',
      new BehaviorSubject(null),
    );
    this.uiActions.setUiTheme(XUX_LIST_THEME_CLASS);

    this.subscriptions.push(
      this.title$
        .pipe(
          rx.distinctUntilChanged(),
          rx.filter((title: string) => !!title),
        )
        .subscribe((title: string): void => {
          this.headerName = title;
        }),
    );
  }

  private composeMasterQuery(
    query: IFilterQuery,
    options: IDocumentOperationOptions,
  ): IRequestPayload {
    if (this.inShowDeletedMode) {
      return { query, options };
    }
    const notNeedActive = get(query, 'notNeedActive', false);
    const activeFilter: string = `${notNeedActive ? '' : 'and (is_active eq in(true))'}`;
    const extendedFilterWithActive: string =
      has(query, 'filter') && !isEmpty(query.filter)
        ? `${query.filter}${activeFilter}`
        : activeFilter;

    return omit(
      {
        options,
        query: {
          ...query,
          include_inactive: true,
          select: union(query.select, ['updated_at']),
          filter: extendedFilterWithActive,
        },
      },
      notNeedActive ? ['query.notNeedActive', 'query.include_inactive'] : [],
    );
  }

  protected updatePageSideTotal(): void {
    // Update pageSideTotal for client-side filtering after items are loaded and filters are applied
    const enableServerSideFilter =
      this.xuxGridComponent.gridOptions?.enableServerSideFilter;

    if (!this.isServerFilter(enableServerSideFilter)) {
      this.pageSideTotal =
        (this.isTreeViewMode
          ? chain(getAllRowsAfterFilterAndSort(this.gridApi))
              .filter((node) => {
                return isEqual(this.treeTotalLevel, Number.POSITIVE_INFINITY) ||
                  isNil(this.treeTotalLevel)
                  ? isLeaf(node)
                  : isEqual(node.level, this.treeTotalLevel) ||
                      isEqual(node.uiLevel, this.treeTotalLevel);
              })
              .size()
              .value()
          : this.gridApi?.getDisplayedRowCount()) || 0;
    }
  }

  protected updateActionButtonsStatus(): void {
    const buttons = this.actionButtonsConfig
      .filter((btn) => btn.value !== BTN_VIEW_DETAILS.value)
      .map((btn) => ({
        ...btn,
        caption: isFunction(btn.caption)
          ? btn.caption.call(this, this.selectedItems)
          : btn.caption,
        disabled: isFunction(btn.disabled)
          ? btn.disabled.call(this, this.selectedItems)
          : get(btn, 'disabled', false),
        hidden: isFunction(btn.hidden)
          ? btn.hidden.call(this, this.selectedItems)
          : get(btn, 'hidden', false),
      }))
      .filter((btn) => !btn.hidden);

    const i18nButtons = this.i18nService.translateButtonsText(buttons);

    // moves create buttons out of actions array
    this.createButton = head(remove(i18nButtons, { value: BTN_CREATE.value }));
    this.printButton = head(remove(i18nButtons, { value: BTN_PRINT.value }));
    this.exportButton = head(remove(i18nButtons, { value: BTN_EXPORT.value }));
    this.eventSelectorButton = head(
      remove(i18nButtons, { value: BTN_SELECT_EVENT.value }),
    );
    this.siteSelectorButton = head(
      remove(i18nButtons, { value: BTN_SELECT_SITE.value }),
    );
    this.siteAndEventSelectorButton = head(
      remove(i18nButtons, { value: BTN_SELECT_SITE_AND_EVENT.value }),
    );
    this.cancelStandWorkSheetButton = head(
      remove(i18nButtons, { value: BTN_CANCEL_OPEN_STAND_WORKSHEET.value }),
    );
    this.printOpeningSheetsButton = head(
      remove(i18nButtons, { value: BTN_PRINT_OPENING_SHEETS.value }),
    );
    this.autoRestocksButton = head(
      remove(i18nButtons, { value: BTN_AUTO_RESTOCKS.value }),
    );
    this.printSheetButton = head(
      remove(i18nButtons, { value: BTN_PRINT_SHEET.value }),
    );

    // override all action button class to '',
    this.actionButtons = this.getActiveButtons(
      map(i18nButtons, (button) => ({ ...button, class: '' })),
    );
    this.rowActionButtons = [{ ...BTN_VIEW_DETAILS }];
  }

  private getActiveButtons(
    buttons: IToolbarButtonDefinition[],
  ): IToolbarButtonDefinition[] {
    if (isEmpty(buttons)) return [];

    const positionGroupedButtons = groupBy(
      buttons,
      (button: IToolbarButtonDefinition) =>
        isEqual(button.isFlattenButtonToActionPanel, true)
          ? 'flattenButtons'
          : 'dropdownButtons',
    );

    const dropdownActionButtons = get(
      positionGroupedButtons,
      'dropdownButtons',
      [],
    );
    const flattenActionButtons = get(
      positionGroupedButtons,
      'flattenButtons',
      [],
    );

    return concat(
      this.getDropdownActionButtons(dropdownActionButtons),
      flattenActionButtons,
    );
  }

  private getDropdownActionButtons(
    buttons: IToolbarButtonDefinition[],
  ): IToolbarButtonDefinition[] {
    if (isEmpty(buttons)) return [];

    const isEditButton = (button: IToolbarButtonDefinition): boolean =>
      button.value === BUTTON_ACTIONS.EDIT;
    const editButton: IToolbarButtonDefinition = find(buttons, isEditButton);
    const selectedItemsLength = get(this.selectedItems, 'length');

    if (!get(editButton, 'hidden', true) && selectedItemsLength > 1) {
      const isSelectedItemsLengthMoreThanLimit =
        selectedItemsLength > this.getMaxDocumentsLimit();
      const editButtonDisabled = isSelectedItemsLengthMoreThanLimit;
      const editButtonTooltip = this.composeEditButtonTooltip(
        isSelectedItemsLengthMoreThanLimit,
      );

      const getEditButtonCaption = (): string =>
        `${BTN_EDIT.caption} (${get(this.selectedItems, 'length')})`;

      return [
        {
          disabled: editButtonDisabled,
          caption: getEditButtonCaption(),
          value: BUTTON_ACTIONS.EDIT,
          class: 'btn-default pull-left',
          splitDropdown: true,
          tooltip: editButtonTooltip,
          childItems: reject(buttons, isEditButton),
          flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
        },
      ];
    }

    return [
      {
        flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
        caption: this.i18nService.instant(I18n.Common.Actions),
        value: 'actions',
        childItems: buttons,
      },
    ];
  }

  private composeEditButtonTooltip(
    isSelectedItemsLengthMoreThanLimit: boolean,
  ): string {
    if (isSelectedItemsLengthMoreThanLimit) {
      return this.i18nService.translate(
        I18n.InfoMessages.UnableToEditMoreThanLimitItems,
        {
          limit: this.getMaxDocumentsLimit(),
        },
      );
    }

    return this.i18nService.translate(
      I18n.InfoMessages.UnableToEditMultipleEntitiesWithDifferentFields,
      {
        entityType: this.i18nService.translate(
          getSingularEntityName(this.entityService.config.ENTITY_TYPE),
        ),
        typeField: get(
          this.entityService.config,
          'multiEditAcceptedProperties',
          [],
        )
          .toString()
          .replace(/_/g, ' '),
      },
    );
  }

  private getMaxDocumentsLimit(): number {
    return MULTI_DOCUMENT_EDITING_MAX_DOCUMENTS_LIMIT;
  }

  /**
   * Adds Expand/Collapse All buttons in tree view mode
   * And Sets up actions to update the button states accordingly
   *
   * @param defaultButtons
   * @returns defaultButtons
   */
  private addTreeModeBtnsActions(
    defaultButtons: IToolbarButtonDefinition[],
  ): IToolbarButtonDefinition[] {
    if (!this.gridOptions.treeViewMode) {
      return defaultButtons;
    }

    if (this.gridOptions.enableDynamicExpandBtn) {
      const defaultExpanded = this.gridOptions.defaultExpanded ?? false;
      const isGridCollapsed$ = new BehaviorSubject<boolean>(!defaultExpanded);

      defaultButtons.push({
        ...getExpandOrCollapseBtn({
          isGridCollapsed$,
          i18nService: this.i18nService,
        }),
        click: debounce(() => {
          if (isNil(this.gridApi)) {
            return;
          }
          const nextIsCollapsed = !isGridCollapsed$.getValue();

          const customizeExpandFn = get(
            this.entityService,
            'config.list.gridOptions.customizeExpandFn',
            null,
          );

          if (
            !customizeExpandFn ||
            !customizeExpandFn({
              gridApi: this.gridApi,
              isInitExpand: false,
              expanded: !nextIsCollapsed,
              maxLevel: this.gridOptions.expandedMaxLevel,
            })
          ) {
            expandOrCollapseGrid({
              expanded: !nextIsCollapsed,
              gridApi: this.gridApi,
              maxLevel: this.gridOptions.expandedMaxLevel,
            });
          }

          isGridCollapsed$.next(nextIsCollapsed);

          this.gridApi.ensureIndexVisible(0, 'top');
        }, 200),
      });

      const checkIsCollapsed = (
        _action: XuxGridAction,
        xuxList: XUXListContainerComponent,
      ): void => {
        const actionButtons = this.actionButtons;

        const expandCollapseBtn = find(actionButtons, {
          value: BUTTON_ACTIONS.EXPAND_COLLAPSE,
        });

        if (!expandCollapseBtn || expandCollapseBtn.hidden === true) {
          return;
        }

        isGridCollapsed$.next(
          includes(
            [GridExpandedState.Collapsed, GridExpandedState.Varies],
            getCollapsedOrExpanded(xuxList.gridApi),
          ),
        );
      };

      set(
        this.selfDefaultActions,
        XUX_GRID_ACTIONS.LIST_GROUP_COLLAPSED,
        checkIsCollapsed,
      );
      set(
        this.selfDefaultActions,
        XUX_GRID_ACTIONS.LIST_GROUP_EXPANDED,
        checkIsCollapsed,
      );
    }

    return defaultButtons;
  }

  private initActionButtonsConfig(): void {
    const configButtons = get(this.entityService, 'config.buttons');

    const modifiedButtons = pickBy(configButtons, Boolean);
    const removedButtonIds = keys(omitBy(configButtons, Boolean));

    const defaultButtons: IToolbarButtonDefinition[] = [];

    // default predicates definition
    const notOneItemSelected = (rowData): boolean =>
      get(rowData, 'length') !== 1;
    const noSelection = (rowData): boolean => isEmpty(rowData);
    const isEditHidden = (rowData): boolean =>
      noSelection(rowData) || notOneItemSelected(rowData);

    this.showEventSelector = !!configButtons?.select_event;
    this.showSiteSelector = !!configButtons?.select_site;
    this.showPrintButton = !!configButtons?.print;
    this.showExportButton = !!configButtons?.export;
    this.showSiteAndEventSelector = !!configButtons?.select_site_and_event;
    this.showCancelOpeningSheetBtn =
      !this.isHistoryQuantitiesRouter &&
      !!configButtons?.cancel_opening_stand_sheet &&
      !this.canAdd &&
      this.canManage;
    this.showPrintOpeningSheets = !!configButtons?.print_opening_sheets;
    this.showPrintSheet = !!configButtons?.print_sheets;

    // check permissions and draw buttons
    if (this.canAdd) {
      defaultButtons.push({
        ...BTN_CREATE,
        caption: this.translateService.instant(I18n.Common.NewEntity, {
          entityName: this.translateService.instant(
            getSingularEntityName(this.entityType),
          ),
        }),
      });
    }

    if (this.canManage) {
      defaultButtons.push({ ...BTN_EDIT, hidden: isEditHidden });
    }

    if (!this.canManage && this.canView) {
      defaultButtons.push({
        ...BTN_EDIT,
        hidden: isEditHidden,
        caption: I18n.Common.View,
      });
    }

    if (this.canDelete) {
      defaultButtons.push({ ...BTN_REMOVE, hidden: noSelection });
    }

    if (!this.isHiddenRefreshButton) {
      defaultButtons.push({
        ...BTN_REFRESH,
        isFlattenButtonToActionPanel: true,
      });
    }

    this.addRemoveDeleteItemsViewButton(defaultButtons);
    defaultButtons.push({ ...BTN_CANCEL_OPEN_STAND_WORKSHEET });

    defaultButtons.push({ ...BTN_VIEW_DETAILS });
    defaultButtons.push({ ...BTN_SELECT_EVENT });
    defaultButtons.push({ ...BTN_SELECT_SITE });
    defaultButtons.push({ ...BTN_PRINT_OPENING_SHEETS });
    defaultButtons.push({ ...BTN_PRINT_SHEET });

    this.addTreeModeBtnsActions(defaultButtons);

    // adds default handler to buttons
    const defaultHandler = (selectedItems, handler): any =>
      handler.call(this, selectedItems);

    this.actionButtonsConfig = defaultButtons
      .filter(({ value }) => !removedButtonIds.includes(value))
      .map((button) => {
        // keep original handler for expand/collapse and remove buttons
        const newButton = includes(
          [BUTTON_ACTIONS.EXPAND_COLLAPSE, BUTTON_ACTIONS.REMOVE],
          button.value,
        )
          ? button
          : set(button, 'click', defaultHandler);

        return this.i18nService.translateButtonText(newButton);
      });

    const buttonsVisibilityObj: { [key: string]: boolean } = {
      [BTN_CREATE.value]: this.canAdd,
      [BTN_REMOVE.value]: this.canDelete,
      [BTN_EDIT.value]: this.canManage,
      [BTN_REFRESH.value]: !this.isHiddenRefreshButton,
    };
    // merge buttons
    each(modifiedButtons, (properties, value) => {
      if (has(buttonsVisibilityObj, value) && !buttonsVisibilityObj[value]) {
        return;
      }

      const defaultButton = find(this.actionButtonsConfig, { value });
      if (defaultButton) {
        merge(defaultButton, properties);
      } else {
        this.actionButtonsConfig.push({
          value,
          ...properties,
          click: (selectedItems): any => {
            if (isNil(properties.click)) {
              return defaultHandler;
            }

            // pass current context to click function
            return properties.click.call(this, selectedItems, this);
          },
        });
      }
    });

    this.updateActionButtonsStatus();
  }

  private async actionButtonClicked(value, rowData): Promise<void> {
    const button = find(this.actionButtonsConfig, { value });
    const handler = get(button, 'click');

    const handlersNames = {
      [BTN_CREATE.value]: 'createBtnClick',
      [BTN_EDIT.value]: 'editButtonClick',
      [BTN_REMOVE.value]: 'removeButtonClick',
      [BTN_SHOW_DELETED_ITEMS.value]: 'showDeletedButtonClick',
      [BTN_VIEW_DETAILS.value]: 'viewDetailsButtonClick',
      [BTN_REFRESH.value]: 'refresh',
    };

    const originalHandler = this[handlersNames[value]];
    const isRemoveButton = value === BTN_REMOVE.value;

    if (!button) return;

    if (isRemoveButton) {
      const func = handler ? handler.bind(this) : null;

      return originalHandler.call(this, rowData, func);
    }

    if (!isFunction(handler)) return;

    return handler.call(this, rowData, (originalHandler || noop).bind(this));
  }

  private populateDefaultEntitiesColumns(
    listColumns: IXVIListColumn[],
    isDefaultColumnsVisible: boolean,
  ): IXVIListColumn[] {
    if (!isDefaultColumnsVisible) {
      return this.i18nService.translateTextInColumns(listColumns);
    }

    DEFAULT_ENTITIES_COLUMNS.forEach((column: IXVIListColumn) => {
      if (isEqual(column.path, MODIFIED_DATE_COLUMN.path)) {
        column.defaultView = get(
          this.entityService,
          ['config', 'list', 'isShowModifiedDateByDefault'],
          false,
        );

        column.headerComponentParams = {
          ...column.headerComponentParams,
          enableMenu: !get(
            this.entityService,
            ['config', 'list', 'isHideModifiedDateFilter'],
            false,
          ),
        };

        column.valueFormatter = ({ value }): string =>
          this.entityService.companyService.convertUTCDateWithTimeZone(
            value,
            true,
            TIME_FORMATS.L,
          );
      }

      const columnIndex = findIndex(listColumns, { path: column.path });

      if (columnIndex !== -1) {
        return (listColumns[columnIndex] = {
          ...listColumns[columnIndex],
          ...column,
        });
      }
      listColumns.push(column);
    });

    return this.i18nService.translateTextInColumns(listColumns);
  }

  private filterHiddenColumns(listColumns: IXVIListColumn[]): IXVIListColumn[] {
    return filter(
      this.applyHiddenConditions(listColumns),
      (listColumn) => !listColumn.hide,
    );
  }

  private applyHiddenConditions(
    listColumns: IXVIListColumn[],
  ): IXVIListColumn[] {
    return map(listColumns, (listColumn) => {
      const xuxListColumn = Object.assign({}, listColumn);
      const { hideConditions } = xuxListColumn;
      unset(xuxListColumn, 'hideConditions');
      unset(xuxListColumn, 'hide');

      if (isPlainObject(hideConditions)) {
        const conditionsArr = toPairs(hideConditions);
        const conditionResult = some(conditionsArr, ([key, value]) =>
          this.checkHideCondition(key, value),
        );
        if (conditionResult) {
          set(xuxListColumn, 'hide', conditionResult);
        }
      }

      return xuxListColumn;
    });
  }

  private checkHideCondition(key: string, value: any): boolean {
    switch (key) {
      case HIDE_COLUMN_CONDITIONS.ALWAYS_HIDE:
        return true;
      case HIDE_COLUMN_CONDITIONS.MASTER:
        return isEmpty([]) === value;
      default:
        return false;
    }
  }

  private resetPredefinedFilterModel(): void {
    const predefinedFilterModel = this.stateService.getBySelector(
      STATE_SELECTORS.predefinedFilterModelSelector,
    );
    if (isEmpty(predefinedFilterModel)) {
      return;
    }
    const predefinedFilterKeys = keys(predefinedFilterModel);
    const filterModel = omit(this.filterModel, predefinedFilterKeys);
    this.updateListViewDataProxy({
      filterModel,
      entityType: this.entityType,
      predefinedFilterModel: null,
    });
  }

  private addRemoveDeleteItemsViewButton(
    defaultButtons: IToolbarButtonDefinition[],
  ): void {
    const currentDefaultViews = this.stateService.getByPath(
      STATE_SELECTORS.UI_LIST_DEFAULT_VIEWS,
      [],
    );
    const indexOfDeletedItems = currentDefaultViews.indexOf(
      BTN_SHOW_DELETED_ITEMS.value,
    );
    if (indexOfDeletedItems >= 0) {
      currentDefaultViews.splice(indexOfDeletedItems, 1);
    }

    if (this.entityService?.config?.buttons?.remove !== false) {
      defaultButtons.push({ ...BTN_SHOW_DELETED_ITEMS });
      this.uiActions.updateListViewData({
        defaultViews: [BTN_SHOW_DELETED_ITEMS.value, ...currentDefaultViews],
      });
    } else {
      this.uiActions.updateListViewData({ defaultViews: currentDefaultViews });
    }
  }

  private handleFESearch(): void {
    const enableServerSideFilter =
      this.xuxGridComponent.gridOptions?.enableServerSideFilter;
    if (!this.isServerFilter(enableServerSideFilter)) {
      const searchString: string = get(this.viewConfig, 'quickFilter', '');
      if (isEmpty(searchString)) return;
      const actionPayload: XuxGridAction = {
        type: XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED,
        payload: { value: searchString },
      };
      this.xuxGridComponent.emitAction(actionPayload);
    }
  }

  /**
   * this method will called when quick filter value changed & is client side filtering
   *
   * @param payload
   */
  private handleSearch(payload: XuxGridActionPayload): void {
    this.updateListViewData({
      quickFilter: payload.value,
      currentPage: 1,
    });
    this.gridApi?.sizeColumnsToFit();
    handleGridOverlay(this.xuxGridComponent);
    this.updatePageSideTotal();
  }

  private handleItemsArrayChanged(): void {
    if (this.useCustomGrid) {
      this.gridApi?.sizeColumnsToFit();
    }
    this.handleFESearch();

    this.updatePageSideTotal();

    const enableServerSideFilter =
      this.xuxGridComponent.gridOptions?.enableServerSideFilter;
    if (!this.isServerFilter(enableServerSideFilter)) {
      handleGridOverlay(this);
    }

    this.handleCustomizeExpanded({ gridApi: this.gridApi });
  }
  // eslint-disable-next-line max-lines
}
